/* eslint-disable */
import dayjs from 'dayjs';

const TZ = process.env.REACT_APP_TIMEZONE || 'Asia/Seoul';

/**
 * ===============================
 * 기준 시점 (함수형으로 변경하여 호출 시점의 현재 시각 보장)
 * ===============================
 */
const getNow = () => dayjs().tz(TZ);

/**
 * ===============================
 * 날짜 비교 유틸 (Dayjs 객체 기반)
 * ===============================
 */
// 데이터 d가 { year, month, day } 형태의 객체일 때 비교 로직
export const isSameDay = (d, base) => {
  const target = dayjs.tz(`${d.year}-${d.month}-${d.day}`, TZ);
  return target.isSame(base, 'day');
};

export const isToday = (d) => isSameDay(d, getNow());

export const isYesterday = (d) => isSameDay(d, getNow().subtract(1, 'day'));

export const isThisMonth = (d) => {
  const target = dayjs.tz(`${d.year}-${d.month}-01`, TZ);
  return target.isSame(getNow(), 'month');
};

export const isPrevMonth = (d) => {
  const target = dayjs.tz(`${d.year}-${d.month}-01`, TZ);
  return target.isSame(getNow().subtract(1, 'month'), 'month');
};

export const isThisYear = (d) => d.year === getNow().year();

export const isPrevYear = (d) => d.year === getNow().year() - 1;

/**
 * ===============================
 * 기준 날짜 가공 유틸
 * ===============================
 */
export function getBaseDate(type, target, baseDate = getNow()) {
  let d = dayjs.tz(baseDate, TZ);

  if (target === "PREV") {
    d = d.subtract(1, type.toLowerCase()); // type이 'DAY'면 'day' 빼기
  }

  const formats = {
    DAY: "YYYY.MM.DD",
    MONTH: "YYYY.MM",
    YEAR: "YYYY"
  };

  return {
    year: d.year(),
    month: d.month() + 1,
    day: d.date(),
    value: d.format(formats[type])
  };
}

/**
 * ===============================
 * X축 생성
 * ===============================
 */
export function buildTimeAxis(tab) {
    if (tab === "DAY") {
        return Array.from({ length: 24 }, (_, i) =>
            String(i).padStart(2, "0")
        );
    }

    if (tab === "MONTH") {
        // 월별 데이터 비교를 위해 항상 1~31일까지 생성
        return Array.from({ length: 31 }, (_, i) => String(i + 1));
    }

    return Array.from({ length: 12 }, (_, i) => String(i + 1));
}

/**
 * ===============================
 * 단위 변환 및 데이터 매핑 (기존 로직 유지하되 비교 함수만 활용)
 * ===============================
 */
export function convertDuration(value, tab) {
  if (value == null) return 0;
  return tab === "DAY" ? +(value / 60).toFixed(1) : +(value / 3600).toFixed(2);
}

export function mapDurationToAxis(axis, data, tab, t) {
  const isTarget = {
    DAY: { prev: isYesterday, curr: isToday },
    MONTH: { prev: isPrevMonth, curr: isThisMonth },
    YEAR: { prev: isPrevYear, curr: isThisYear }
  };

  const prevData = Array(axis.length).fill(0);
  const currData = Array(axis.length).fill(0);
  const fieldMap = { DAY: 'hour', MONTH: 'day', YEAR: 'month' };

  data.forEach(d => {
    const val = fieldMap[tab] === 'hour' ? String(d.hour).padStart(2, "0") : String(d[fieldMap[tab]]);
    const idx = axis.indexOf(val);
    if (idx === -1) return;

    if (isTarget[tab].prev(d)) prevData[idx] += convertDuration(d.durationSum, tab);
    if (isTarget[tab].curr(d)) currData[idx] += convertDuration(d.durationSum, tab);
  });

  return [
    { name: getBaseDate(tab, "PREV").value, data: prevData },
    { name: getBaseDate(tab, "CUR").value, data: currData }
  ];
}

/**
 * ===============================
 * 데이터 → 설비별 series 매핑
 * ===============================
 */
export function mapDurationByEquipmentsToAxis(axis, equipments, data, tab, t) {
  // 1️⃣ 설비 목록 추출 (중복 제거 및 Map 생성)
  const equipmentList = Array.from(
    new Map(equipments.map(d => [d.id, d.name]))
  );

  // 2️⃣ 설비별 series 생성
  return equipmentList.map(([equipmentId, equipmentName]) => {
    const values = Array(axis.length).fill(0);

    data.forEach(d => {
      // 해당 설비 데이터가 아니면 패스
      if (d.equipmentId !== equipmentId) return;

      let idx = -1;
      const hourStr = String(d.hour || 0).padStart(2, "0");

      // 탭에 따른 날짜 필터링 및 인덱스 추출
      if (tab === "DAY") {
        if (!isToday(d)) return; // 위에서 만든 isToday 활용
        idx = axis.indexOf(hourStr);
      } else if (tab === "MONTH") {
        if (!isThisMonth(d)) return; // 위에서 만든 isThisMonth 활용
        idx = axis.indexOf(String(d.day));
      } else if (tab === "YEAR") {
        if (!isThisYear(d)) return; // 위에서 만든 isThisYear 활용
        idx = axis.indexOf(String(d.month));
      }

      if (idx !== -1) {
        values[idx] += convertDuration(d.durationSum, tab);
      }
    });

    return {
      name: equipmentName ?? String(equipmentId),
      data: values
    };
  });
}

export function getDurationUnit(tab, t) {
    switch (tab) {
        case "DAY":
            return t("message.common.text.minute"); // "분"
        case "MONTH":
        case "YEAR":
            return t("message.common.text.time");   // "시간"
        default:
            return "";
    }
}

export function getCurrentPeriodLabel(tab) {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");

    switch (tab) {
        case "DAY":
            return `${year}.${month}.${day}`;
        case "MONTH":
            return `${year}.${month}`;
        case "YEAR":
            return `${year}`;
        default:
            return "";
    }
}

/**
 * ===============================
 * ECharts option 생성 (가독성 개선)
 * ===============================
 */
export function buildOption(axis, series, textColor, bgColor, tab, t, graphType = 'line') {
  if (!axis || !series) return {};

  const unit = getDurationUnit(tab, t);

  // 분 단위로 변환하여 시간 표시
  const formatDuration = (value) => {
    const rawValue = Number(value) || 0;

    const totalMinutes = Math.round(
        tab === "DAY" ? rawValue : rawValue * 60
    );

    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;

    if (h === 0 && m === 0) return "0분";
    if (h === 0) return `${m}${t("message.common.text.minute")}`;
    if (m === 0) return `${h}${t("message.common.text.time")}`;

    return `${h}${t("message.common.text.time")} ${m}${t("message.common.text.minute")}`;
  };

  return {
      textStyle: { color: textColor },

      tooltip: {
          trigger: "axis",
          confine: false,
          appendToBody: true,
          axisPointer: { type: "shadow" },

          formatter: (params) => {
              if (!params || !params.length) return "";

              const axisLabel = params[0].axisValueLabel;

              const header = `<strong>${axisLabel}</strong>`;

              const body = params
                  .filter(p => p.value != null)
                  .map(p =>
                      `${p.marker} ${p.seriesName} : <b>${formatDuration(p.value)}</b>`
                  )
                  .join("<br/>");

              return `${header}<br/>${body}`;
          }
      },

      legend: {
          left: 'center',
          top: 8,
          backgroundColor: bgColor,
          textStyle: { color: textColor }
      },

      grid: {
          left: '3%',
          right: '3%',
          bottom: '3%',
          containLabel: true
      },

      xAxis: {
          type: "category",
          boundaryGap: true,
          data: axis,
          axisLabel: { color: textColor }
      },

      yAxis: {
          type: "value",
          name: unit,
          axisLabel: { color: textColor, margin: 8 }
      },

      series: series.map((s, index) => ({
          id: s.id || `series_${index}`,
          name: s.name,
          type: graphType,
          smooth: true,
          showSymbol: false,
          data: s.data,
          lineStyle: { width: 2 }
      }))
  };
}